import React, {useEffect, useState} from "react";
import Select from "react-select";

// XPEN-3697: opened over the MUI approval dialog, so it must be an MUI dialog too.
// The legacy components/modal sits outside that dialog's focus trap, which takes
// the keyboard back from its fields and hides it from assistive technology.
import Modal from "../../components/modal_dialog";
import {fetch} from "../../utils/api-wrapper";
import {duplicateApprovalMessage} from "../../utils/expense-duplicate-message";


const EXISTING_ACCOUNT_MODE = "existing";
const NEW_ACCOUNT_MODE = "new";

const translate = (data, key, fallback) => {
    const value = data?.t?.read?.(key);
    return typeof value === "string" || typeof value === "number" ? value : fallback;
};

/**
 * Lets an invoice owner move a denied XPENSE expense into an owned draft account.
 * The API resolves the denied request again so browser state is never an authority.
 *
 * @param {object} props
 * @param {object} props.data - XPENS store data used for translations.
 * @param {string} props.requestId - Stable denied approval history request ID.
 * @param {Function} props.onCancel - Closes the configuration modal.
 * @param {Function} props.onMoved - Receives the successful move response.
 */
const DeniedExpenseMoveModal = ({data, requestId, onCancel, onMoved}) => {
    const [mode, setMode] = useState(EXISTING_ACCOUNT_MODE);
    const [accounts, setAccounts] = useState([]);
    const [selectedAccountId, setSelectedAccountId] = useState("");
    const [description, setDescription] = useState("");
    const [loading, setLoading] = useState(true);
    const [moving, setMoving] = useState(false);
    const [error, setError] = useState(null);
    const [accountMenuOpen, setAccountMenuOpen] = useState(false);

    useEffect(() => {
        let isCurrent = true;
        setLoading(true);
        setError(null);
        fetch(
            `/approvals/denied/expense-move-options?request_id=${encodeURIComponent(requestId)}`,
            "GET",
            {},
            (response) => {
                if (!isCurrent) return;
                setAccounts(response?.data?.accounts ?? []);
                setLoading(false);
            },
            () => {
                if (!isCurrent) return;
                setError(translate(
                    data,
                    "approval_modal.move_expense_load_error",
                    "Expense accounts could not be loaded."
                ));
                setLoading(false);
            },
        );
        return () => {
            isCurrent = false;
        };
    }, [data, requestId]);

    const isExistingSelectionValid = mode === EXISTING_ACCOUNT_MODE && Boolean(selectedAccountId);
    const isNewAccountValid = mode === NEW_ACCOUNT_MODE && Boolean(description.trim());
    const invalid = loading || moving || (!isExistingSelectionValid && !isNewAccountValid);
    const accountOptions = accounts.map((account) => ({
        value: account.id,
        label: [account.identifiant, account.description].filter(Boolean).join(" - "),
    }));
    const selectedAccount = accountOptions.find((option) => option.value === selectedAccountId) ?? null;

    const moveExpense = () => {
        if (invalid) return false;
        setMoving(true);
        setError(null);
        const payload = mode === EXISTING_ACCOUNT_MODE
            ? {request_id: requestId, destination_account_id: selectedAccountId}
            : {request_id: requestId, new_account_description: description.trim()};
        fetch(
            "/approvals/denied/move-expense",
            "POST",
            payload,
            (response) => {
                setMoving(false);
                onMoved(response?.data ?? {});
            },
            (response) => {
                setMoving(false);
                setError(duplicateApprovalMessage(data, response?.response?.data, "requester") || translate(
                    data,
                    "approval_modal.move_expense_error",
                    "Expense could not be moved."
                ));
            },
        );
        // Modal owns an internal processing flag. Returning false keeps it in
        // sync with the explicit moving state used by this asynchronous form.
        return false;
    };

    // Only the choice the user can still see counts as unsaved input.
    const hasUnsavedInput = mode === EXISTING_ACCOUNT_MODE
        ? Boolean(selectedAccountId)
        : Boolean(description.trim());

    // react-select closes its menu on Escape but lets the key bubble to the dialog,
    // which would close the whole window too.
    const keepEscapeInOpenMenu = (event) => {
        if (event.key === "Escape" && accountMenuOpen) event.stopPropagation();
    };

    return (
        <Modal
            title={translate(data, "approval_modal.move_expense_title", "Move expense")}
            // While the move is sent, no path may close the window: without a cancel
            // action the dialog hides its cancel button and close icon.
            cancelAction={moving ? undefined : onCancel}
            disableEscapeClose={moving}
            disableBackdropClose={moving}
            cancelText={translate(data, "cancel", "Cancel")}
            okAction={moveExpense}
            okText={moving
                ? `${translate(data, "please_wait", "Please wait")}...`
                : translate(data, "approval_modal.move_expense_confirm", "Move expense")}
            invalid={invalid}
            isDirty={hasUnsavedInput}
            noProcessingLoading
            overflow="visible"
        >
            <fieldset disabled={loading || moving}>
                <legend className="sr-only">
                    {translate(data, "approval_modal.move_expense_destination_mode", "Destination type")}
                </legend>
                <div className="form-check mb-2">
                    <input
                        id="move-expense-existing-account"
                        className="form-check-input"
                        type="radio"
                        name="move-expense-mode"
                        value={EXISTING_ACCOUNT_MODE}
                        checked={mode === EXISTING_ACCOUNT_MODE}
                        onChange={() => setMode(EXISTING_ACCOUNT_MODE)}
                    />
                    <label className="form-check-label" htmlFor="move-expense-existing-account">
                        {translate(data, "approval_modal.move_existing_account", "Existing expense account")}
                    </label>
                </div>
                <div className="form-check mb-3">
                    <input
                        id="move-expense-new-account"
                        className="form-check-input"
                        type="radio"
                        name="move-expense-mode"
                        value={NEW_ACCOUNT_MODE}
                        checked={mode === NEW_ACCOUNT_MODE}
                        onChange={() => setMode(NEW_ACCOUNT_MODE)}
                    />
                    <label className="form-check-label" htmlFor="move-expense-new-account">
                        {translate(data, "approval_modal.move_new_account", "New expense account")}
                    </label>
                </div>
            </fieldset>

            {mode === EXISTING_ACCOUNT_MODE && (
                <div className="form-group">
                    <label htmlFor="move-expense-destination-account">
                        {translate(data, "approval_modal.destination_account", "Destination expense account")}
                    </label>
                    <Select
                        inputId="move-expense-destination-account"
                        classNamePrefix="select"
                        value={selectedAccount}
                        options={accountOptions}
                        onChange={(option) => setSelectedAccountId(option?.value ?? "")}
                        isDisabled={loading || moving || accounts.length === 0}
                        isClearable
                        // Floats the menu over the dialog instead of growing its scroll
                        // area. Not a body portal: that would leave the focus trap again.
                        menuPosition="fixed"
                        onMenuOpen={() => setAccountMenuOpen(true)}
                        onMenuClose={() => setAccountMenuOpen(false)}
                        onKeyDown={keepEscapeInOpenMenu}
                        placeholder={translate(
                            data,
                            "approval_modal.select_destination_account",
                            "Select an expense account"
                        )}
                    />
                    {!loading && accounts.length === 0 && (
                        <div className="text-muted mt-2">
                            {translate(
                                data,
                                "approval_modal.no_pending_expense_accounts",
                                "No draft expense accounts are available."
                            )}
                        </div>
                    )}
                </div>
            )}

            {mode === NEW_ACCOUNT_MODE && (
                <div className="form-group">
                    <label htmlFor="move-expense-new-account-description">
                        {translate(data, "approval_modal.new_account_description", "New account description")}
                    </label>
                    <input
                        id="move-expense-new-account-description"
                        className="form-control"
                        type="text"
                        value={description}
                        onChange={(event) => setDescription(event.target.value)}
                        disabled={moving}
                    />
                </div>
            )}

            {loading && <div className="text-muted">{translate(data, "please_wait", "Please wait")}...</div>}
            {error && <div className="alert alert-danger mb-0" role="alert">{error}</div>}
        </Modal>
    );
};

export default DeniedExpenseMoveModal;
